import type { OrderPersonalData } from '@/components/OrderPersonalData/OrderPersonalDataForm';
import type { Incentive } from '@/graphql/types';
import { trackEvent } from '@/helper/clientTelemetry';
import tracking from '@/helper/tracking';
import type { TrackingInformation } from '@/helper/tracking';
import type { Meta, SelfServiceRegistration } from '@/stores/OrderStore';
import { buildKwkCtaLink } from '@/storyblok/PageContent';
import type { GlobalOrderformSettingsStoryblok } from '@/types/component-types-sb';
import type { Tariff } from '@/types/order';
import { useEffect, useRef } from 'react';

export function usePurchaseTracking({
  id,
  setMeta,
  reset,
  resetEdited,
  resetSinglePriceCalculator,
  basicSupplyTrackingInformation,
  trackingInformation,
  isBasicSupply,
  tariff,
  selfServiceRegistration,
  personalData,
  isKwKOrder,
  kwkCode,
  incentive,
  thankYouPageUid,
  basicSupplyThankYouUid,
  currentPageUid,
  orderSettings,
  setKwkThankYouCta,
}: {
  id: string;
  setMeta: (key: keyof Meta, value: string) => void;
  reset: () => void;
  resetEdited: () => void;
  resetSinglePriceCalculator: () => void;
  basicSupplyTrackingInformation: TrackingInformation[] | undefined;
  trackingInformation: TrackingInformation | undefined;
  isBasicSupply: boolean;
  tariff: Tariff | undefined;
  selfServiceRegistration: SelfServiceRegistration | undefined;
  personalData: OrderPersonalData;
  isKwKOrder: boolean | undefined;
  kwkCode: string | undefined;
  incentive: Incentive | undefined;
  thankYouPageUid: string | undefined;
  basicSupplyThankYouUid: string | undefined;
  currentPageUid: string;
  orderSettings: GlobalOrderformSettingsStoryblok | null | undefined;
  setKwkThankYouCta: (cta: { link: string; text: string }) => void;
}): void {
  const purchaseTracking = useRef(false);

  useEffect(() => {
    const finalBasicSupplyTrackingInformation = basicSupplyTrackingInformation || [];
    const finalTrackingInformation = trackingInformation ? [trackingInformation] : [];
    const finalTrackingData = isBasicSupply ? finalBasicSupplyTrackingInformation : finalTrackingInformation;

    if (!purchaseTracking.current && finalTrackingData.length > 0) {
      if (thankYouPageUid === currentPageUid || basicSupplyThankYouUid === currentPageUid) {
        // Capture KwK thank-you CTA from store before reset() clears isKwKOrder/kwkCode
        if (
          thankYouPageUid === currentPageUid &&
          isKwKOrder &&
          orderSettings?.kwk_landing_page &&
          orderSettings?.kwk_cta_button_thank_you
        ) {
          const link = buildKwkCtaLink(orderSettings.kwk_landing_page, kwkCode);
          setKwkThankYouCta({ link, text: orderSettings.kwk_cta_button_thank_you });
        }

        let yearlyPrice = 0;
        if (isBasicSupply) {
          finalBasicSupplyTrackingInformation.forEach((trackingItem) => {
            if (!trackingItem.price) return;
            yearlyPrice += trackingItem.price;
          });
        } else {
          yearlyPrice = tariff?.prices?.yearlyPrice ?? 0;
        }

        tracking.productPurchase(finalTrackingData, {
          transactionId: id,
          revenue: parseFloat(yearlyPrice.toFixed(2)),
          seSeRegistration: selfServiceRegistration?.consent || false,
          allowMarketing: personalData.marketingOptIn,
          affiliation: isKwKOrder ? 'kwk' : 'website',
          coupon: incentive ? incentive.name.toLowerCase() : '',
        });
        trackEvent('Order_Complete', {
          step: 'thank_you',
          is_basic_supply: isBasicSupply,
        });
        purchaseTracking.current = true;
        setMeta('id', '');

        if (personalData) {
          reset();
          resetEdited();
          resetSinglePriceCalculator();
        }
      }
    }
  }, [
    basicSupplyTrackingInformation,
    trackingInformation,
    isBasicSupply,
    tariff,
    selfServiceRegistration,
    personalData,
    isKwKOrder,
    kwkCode,
    incentive,
    thankYouPageUid,
    basicSupplyThankYouUid,
    currentPageUid,
    orderSettings,
    id,
    reset,
    resetEdited,
    resetSinglePriceCalculator,
    setMeta,
    setKwkThankYouCta,
  ]);
}
